// Achievements tab (Parent Dashboard): every child's certificates in one
// gallery. New ones glow with a pulsing "New" tag + golden border until the
// visit marks them seen (badge dismissal). Print PDF embeds the Audio QR
// (+ Family Passcode) when the child has an active shared reading; Share
// Image exports the 1080px PNG via native share or download.
import { createElement, useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { QRCodeSVG } from "qrcode.react";
import { Award, Printer, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { api } from "../../lib/api";
import { printCertificate } from "../../lib/printCert";
import { CertificateSVG, certificatePng } from "../dashboard/MilestoneCertificate";

export const AchievementsPanel = ({ onSeen }) => {
  const [items, setItems] = useState(null);
  const [inkSaver, setInkSaver] = useState(false);
  const svgRefs = useRef({});

  useEffect(() => {
    api.get("/achievements/list").then(({ data }) => {
      setItems(data.items);
      // Dismissal logic: visiting the tab clears the nav badge counter.
      api.post("/achievements/seen").then(() => onSeen?.()).catch(() => {});
    }).catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const qrBlock = (c) => {
    if (!c.audio_share?.token) return {};
    const url = `${window.location.origin}/vault/shared/${c.audio_share.token}`;
    return {
      qrMarkup: ReactDOMServer.renderToStaticMarkup(createElement(QRCodeSVG,
        { value: url, size: 150, level: "M", marginSize: 2 })),
      label: `\u{1F3A7} Scan to hear ${c.first_name || c.child_name}'s reading!`,
      passcode: c.audio_share.pin || "",
    };
  };
  const printIt = (c) => {
    const node = svgRefs.current[c.key];
    if (!node) return;
    const ok = printCertificate({
      svgMarkup: new XMLSerializer().serializeToString(node),
      ...qrBlock(c), inkSaver, title: c.headline,
    });
    if (!ok) toast.error("Pop-up blocked — allow pop-ups to print.");
  };
  const shareImg = async (c) => {
    const node = svgRefs.current[c.key];
    if (!node) return;
    try {
      const blob = await certificatePng(node);
      const file = new File([blob], "certificate.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: c.headline });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "certificate.png"; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success("Certificate image downloaded!");
      }
    } catch { /* user closed the share sheet */ }
  };

  if (items === null) return <p className="text-sm text-muted-foreground p-4">Loading achievements...</p>;
  return (
    <div className="space-y-4" data-testid="achievements-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-extrabold text-foreground">Milestone certificates</p>
        <label className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 cursor-pointer">
          <span className="text-xs font-bold text-foreground">Ink-Saver print (black &amp; white)</span>
          <Switch checked={inkSaver} onCheckedChange={setInkSaver} data-testid="ink-saver-toggle" />
        </label>
      </div>
      {items.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="achievements-empty">
          <Award className="h-10 w-10 mx-auto text-muted-foreground mb-3" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Certificates will appear here as your children reach milestones!</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((c) => (
          <div key={c.key}
            className={`relative rounded-2xl border bg-card p-3 space-y-2 ${c.new ? "border-2 rare-pulse" : "border-border"}`}
            data-testid={`achievement-card-${c.kind}`}>
            {c.new && (
              <span className="absolute -top-2.5 left-3 z-10 rounded-full bg-amber-400 text-amber-950 text-[10px] font-extrabold px-2.5 py-0.5 uppercase tracking-wide animate-pulse" data-testid="achievement-new-tag">New</span>
            )}
            <CertificateSVG svgRef={(n) => { if (n) svgRefs.current[c.key] = n; }}
              name={c.first_name} headline={c.headline} milestone={c.milestone} />
            <p className="text-xs font-bold text-foreground px-1">
              {c.child_name}{c.earned_at ? ` · ${new Date(c.earned_at).toLocaleDateString()}` : ""}
            </p>
            <div className="flex gap-2 px-1 pb-1">
              <Button size="sm" variant="outline" className="rounded-full flex-1 gap-1" onClick={() => printIt(c)} data-testid="achievement-print-btn">
                <Printer className="h-4 w-4" aria-hidden="true" /> Print PDF
              </Button>
              <Button size="sm" variant="outline" className="rounded-full flex-1 gap-1" onClick={() => shareImg(c)} data-testid="achievement-share-btn">
                <Share2 className="h-4 w-4" aria-hidden="true" /> Share Image
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
